import { Handle, Position, NodeProps } from 'reactflow';
import { TableNode as TableNodeType, useSchemaDesignerStore, Column } from '@/stores/schema-designer-store';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  RiDeleteBinLine,
  RiAddLine,
  RiDraggable,
  RiMoreFill,
  RiFileCopyLine,
  RiSettings3Line,
  RiEdit2Line,
} from '@remixicon/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useState } from 'react';
import { AddColumnDialog } from './add-column-dialog';
import { EditColumnDialog } from './edit-column-dialog';
import { EditTableDialog } from './edit-table-dialog';
import { toast } from 'sonner';

interface TableNodeComponentProps extends NodeProps {
  data: TableNodeType['data'];
}

const getTypeColor = (type: string) => {
  const typeColors: Record<string, string> = {
    varchar: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    text: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    integer: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    bigint: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    float: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
    double: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
    decimal: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',
    boolean: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    date: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400',
    datetime: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400',
    timestamp: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400',
    time: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400',
    uuid: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
    json: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    jsonb: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
    enum: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
    array: 'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/30 dark:text-fuchsia-400',
  };
  return typeColors[type] || 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400';
};

export const TableNodeComponent: React.FC<TableNodeComponentProps> = ({ id, data, selected }) => {
  const { removeTable, duplicateTable } = useSchemaDesignerStore();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);

  const handleRemoveTable = () => {
    removeTable(id);
    setShowDeleteDialog(false);
    toast.success(`Table "${data.name}" deleted`);
  };

  const handleDuplicateTable = () => {
    duplicateTable(id);
  };

  return (
    <div className="relative min-w-[320px] max-w-[400px]">
      {/* Source handle (right side - where connections start) */}
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        className={cn(
          'w-3 h-3 bg-primary border-2 border-background transition-all',
          selected && 'ring-2 ring-primary ring-offset-2'
        )}
        style={{ right: '-7px' }}
      />

      {/* Target handle (left side - where connections end) */}
      <Handle
        type="target"
        position={Position.Left}
        id="left"
        className={cn(
          'w-3 h-3 bg-primary border-2 border-background transition-all',
          selected && 'ring-2 ring-primary ring-offset-2'
        )}
        style={{ left: '-7px' }}
      />

      <Card
        className={cn(
          'border-2 shadow-lg overflow-hidden transition-all',
          selected
            ? 'border-primary shadow-primary/20 ring-2 ring-primary/20'
            : 'border-border hover:border-primary/50'
        )}
      >
        {/* Table Header */}
        <CardHeader className="py-3 px-4 bg-muted/50 border-b border-border flex flex-row items-center justify-between space-y-0">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <RiDraggable className="h-4 w-4 text-muted-foreground cursor-grab active:cursor-grabbing flex-shrink-0" />
            <h3 className="text-sm font-semibold text-foreground truncate">{data.name}</h3>
            {data.comment && (
              <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">
                💬
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-1">
            <AddColumnDialog
              tableId={id}
              tableName={data.name}
              trigger={
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                >
                  <RiAddLine className="h-4 w-4" />
                </Button>
              }
            />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                  <RiMoreFill className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                  <AddColumnDialog
                    tableId={id}
                    tableName={data.name}
                    trigger={
                      <div className="flex items-center w-full">
                        <RiAddLine className="h-4 w-4 mr-2" />
                        Add Column
                      </div>
                    }
                  />
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleDuplicateTable}>
                  <RiFileCopyLine className="h-4 w-4 mr-2" />
                  Duplicate Table
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowEditDialog(true)}>
                  <RiSettings3Line className="h-4 w-4 mr-2" />
                  Table Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setShowDeleteDialog(true)}
                  className="text-destructive focus:text-destructive"
                >
                  <RiDeleteBinLine className="h-4 w-4 mr-2" />
                  Delete Table
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>

        {/* Columns List */}
        <CardContent className="p-0">
          <div className="overflow-hidden">
            {data.columns.length === 0 ? (
              <div className="py-8 px-4 text-center text-sm text-muted-foreground">
                <p className="mb-2">No columns yet</p>
                <AddColumnDialog
                  tableId={id}
                  tableName={data.name}
                  trigger={
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2"
                    >
                      <RiAddLine className="h-4 w-4" />
                      Add First Column
                    </Button>
                  }
                />
              </div>
            ) : (
              <div className="divide-y divide-border">
                {data.columns.map((column) => (
                  <ColumnRow key={column.id} column={column} tableId={id} />
                ))}
              </div>
            )}
          </div>
        </CardContent>

        {/* Table Footer - Indexes & Stats */}
        {data.indexes && data.indexes.length > 0 && (
          <div className="px-4 py-2 bg-muted/30 border-t border-border">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="font-medium">{data.indexes.length} index(es)</span>
            </div>
          </div>
        )}
      </Card>

      {/* Edit Table Dialog */}
      <EditTableDialog
        tableId={id}
        tableName={data.name}
        tableComment={data.comment}
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-destructive/10">
                <RiDeleteBinLine className="w-5 h-5 text-destructive" />
              </div>
              Delete Table "{data.name}"?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>This action cannot be undone. This will permanently delete the table and all its columns.</p>
              {data.columns.length > 0 && (
                <div className="p-3 rounded-md bg-muted border border-border">
                  <p className="text-sm font-medium mb-2">This table contains:</p>
                  <ul className="text-sm space-y-1">
                    <li>• {data.columns.length} column{data.columns.length !== 1 ? 's' : ''}</li>
                    {data.indexes && data.indexes.length > 0 && (
                      <li>• {data.indexes.length} index{data.indexes.length !== 1 ? 'es' : ''}</li>
                    )}
                  </ul>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveTable} className="bg-destructive hover:bg-destructive/90">
              Delete Table
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Column Row Component
const ColumnRow: React.FC<{ column: Column; tableId: string }> = ({ column, tableId }) => {
  const { removeColumn, nodes } = useSchemaDesignerStore();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleRemove = () => {
    removeColumn(tableId, column.id);
    setShowDeleteDialog(false);
    toast.success(`Column "${column.name}" deleted`);
  };

  // Get table name for the edit dialog
  const table = nodes.find((node) => node.id === tableId);
  const tableName = table?.data.name || '';

  return (
    <div className="group px-4 py-2.5 hover:bg-muted/50 transition-colors">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium text-foreground truncate">{column.name}</span>
            <Badge className={cn('text-[10px] px-1.5 py-0 h-4', getTypeColor(column.type))}>
              {column.type.toUpperCase()}
              {column.length ? `(${column.length})` : ''}
            </Badge>
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            {column.primaryKey && (
              <Badge variant="default" className="text-[10px] px-1.5 py-0 h-4 bg-primary/10 text-primary border-primary/20">
                PK
              </Badge>
            )}
            {column.unique && !column.primaryKey && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-purple-300 text-purple-700 dark:border-purple-700 dark:text-purple-400">
                UQ
              </Badge>
            )}
            {!column.nullable && !column.primaryKey && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-400">
                NN
              </Badge>
            )}
            {column.autoIncrement && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-green-300 text-green-700 dark:border-green-700 dark:text-green-400">
                AI
              </Badge>
            )}
            {column.defaultValue && (
              <span className="text-[10px] text-muted-foreground">
                = {column.defaultValue}
              </span>
            )}
          </div>

          {column.comment && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
              {column.comment}
            </p>
          )}
        </div>

        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <EditColumnDialog
            tableId={tableId}
            tableName={tableName}
            column={column}
            trigger={
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 text-muted-foreground hover:text-primary"
              >
                <RiEdit2Line className="h-3.5 w-3.5" />
              </Button>
            }
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowDeleteDialog(true)}
            className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
          >
            <RiDeleteBinLine className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Delete Column Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-destructive/10">
                <RiDeleteBinLine className="w-5 h-5 text-destructive" />
              </div>
              Delete Column "{column.name}"?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>This action cannot be undone. This will permanently delete the column from the table.</p>
              <div className="p-3 rounded-md bg-muted border border-border">
                <div className="space-y-1 text-sm">
                  <p><span className="font-medium">Column:</span> {column.name}</p>
                  <p><span className="font-medium">Type:</span> {column.type.toUpperCase()}{column.length ? `(${column.length})` : ''}</p>
                  {column.primaryKey && (
                    <p className="text-destructive font-medium">⚠️ This is a PRIMARY KEY column</p>
                  )}
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemove} className="bg-destructive hover:bg-destructive/90">
              Delete Column
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
